import { useEffect, useMemo, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { LocationSuggestion } from '@superp/shared';
import { api, ApiError } from '../lib/api';

/**
 * Suburb lookup, backed by marwood's Places proxy.
 *
 * Two things here are about money rather than UX. Google bills autocomplete
 * per keystroke unless every request in one typing session carries the same
 * session token — so a token is minted when the field is first used and
 * discarded once a suggestion is picked, and the same token rides along on
 * the details call that resolves the coordinates. And the input is debounced,
 * because a 250ms pause is imperceptible to the person typing and removes
 * roughly two thirds of the calls.
 *
 * `onUnavailable` exists because the lookup can be down in a way retrying
 * will not fix (the tenant has no `places` channel). The parent then shows
 * the plain state picker rather than leaving someone stuck at a field that
 * never suggests anything.
 */

interface SuburbAutocompleteProps {
  onPick: (suggestion: LocationSuggestion, sessionToken: string) => void;
  onUnavailable?: () => void;
  label?: string;
  autoFocus?: boolean;
  helperText?: string;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function SuburbAutocomplete({
  onPick,
  onUnavailable,
  label = 'Your suburb',
  autoFocus,
  helperText,
}: SuburbAutocompleteProps) {
  const [input, setInput] = useState('');
  const debounced = useDebounced(input.trim(), 250);

  // One token per typing session. Regenerated after a pick so the next search
  // is billed as a new session rather than extending the old one forever.
  const [sessionSeed, setSessionSeed] = useState(0);
  const sessionToken = useMemo(
    () => crypto.randomUUID(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionSeed],
  );

  const notifiedUnavailable = useRef(false);

  const { data, isFetching, error } = useQuery({
    queryKey: ['suburb-suggest', debounced, sessionToken],
    // Below two characters Google returns noise and we would still be billed.
    enabled: debounced.length >= 2,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: async () => {
      const params = new URLSearchParams({ q: debounced, sessionToken });
      const res = await api<{ suggestions: LocationSuggestion[] }>(
        `/location/suggest?${params}`,
      );
      return res.suggestions;
    },
  });

  useEffect(() => {
    if (!error || notifiedUnavailable.current) return;
    // 502 is "configured wrong / Google rejected us" — permanent from here.
    // 503 is transient and worth leaving the field alone for.
    if (error instanceof ApiError && error.status === 502) {
      notifiedUnavailable.current = true;
      onUnavailable?.();
    }
  }, [error, onUnavailable]);

  return (
    <Autocomplete
      options={data ?? []}
      // The server already ranked and filtered these; re-filtering client-side
      // would drop matches whose text differs from what was typed.
      filterOptions={(x) => x}
      getOptionLabel={(o) => o.text}
      isOptionEqualToValue={(a, b) => a.placeId === b.placeId}
      loading={isFetching}
      onInputChange={(_, value) => setInput(value)}
      onChange={(_, value) => {
        if (!value) return;
        onPick(value, sessionToken);
        setSessionSeed((n) => n + 1);
      }}
      noOptionsText={
        debounced.length < 2 ? 'Start typing a suburb' : 'No Australian suburbs match'
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          autoFocus={autoFocus}
          helperText={helperText}
          slotProps={{
            ...params.slotProps,
            // MUI v9 moved these off `params.InputProps` and onto
            // `params.slotProps.input`. Spreading the original matters:
            // it carries the ref, the clear/popup buttons and the mousedown
            // handler, so dropping it breaks the dropdown entirely.
            input: {
              ...params.slotProps.input,
              endAdornment: (
                <>
                  {isFetching ? <CircularProgress size={18} /> : null}
                  {params.slotProps.input.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
}
